import axios from 'axios';
import { getMailPassword, clearAllMailPasswords, setMailPassword } from './mailCredentials';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  // Send the ATOZAS SSO session cookie on same-origin /auth/* requests.
  // Harmless for the existing JWT-based API calls (they are same-origin).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isLiveMailEndpoint = (url) => {
  const path = (url || '').split('?')[0];
  return (
    path.includes('/api/mail/server/') ||
    path === '/api/mail/inbox' ||
    path === '/api/mail/counts' ||
    path === '/api/mail/starred' ||
    path === '/api/mail/trash' ||
    path === '/api/mail/send' ||
    // Trash move / permanent delete / restore (e.g. /api/mail/trash/:uid/restore)
    /^\/api\/mail\/trash(\/|$)/.test(path) ||
    /^\/api\/mail\/restore\/[^/]+$/.test(path) ||
    /^\/api\/mail\/server\/trash(\/|$)/.test(path) ||
    /^\/api\/mail\/server\/message\/[^/]+$/.test(path) ||
    /^\/api\/mail\/delete\/[^/]+$/.test(path)
  );
};

const hasGroupSendPayload = (data) => {
  if (!data) return false;
  if (data instanceof FormData) return Boolean(data.get('groupId'));
  return Boolean(data.groupId);
};

const isAtozasHandoffPath = () => {
  const path = window.location.pathname || '';
  return path === '/auth/atozas/finish' || path === '/auth/atozas/setup';
};

const wasExplicitAtozasLogout = () => {
  try {
    return (
      localStorage.getItem('atozas_logged_out') === '1' ||
      sessionStorage.getItem('atozas_logged_out') === '1'
    );
  } catch (_e) {
    return false;
  }
};

/** One in-flight SSO refresh so parallel 401s share a single /me call. */
let ssoRefreshPromise = null;

/**
 * Mint a fresh app JWT from the ATOZAS SSO session cookie when it is still
 * valid. Uses a bare axios call so this never re-enters the 401 interceptor.
 */
const refreshSessionFromAtozasSso = () => {
  if (!ssoRefreshPromise) {
    ssoRefreshPromise = (async () => {
      try {
        if (wasExplicitAtozasLogout()) return false;
        const deviceId = localStorage.getItem('deviceId') || undefined;
        const { data } = await axios.get('/auth/atozas/me', {
          baseURL: process.env.REACT_APP_API_URL || '',
          params: deviceId ? { deviceId } : undefined,
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        });
        if (!data || !data.enabled || !data.authenticated || !data.token || !data.user) {
          return false;
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.mailPassword) {
          setMailPassword(data.mailPassword, data.user.email);
        }
        return true;
      } catch (_e) {
        return false;
      } finally {
        ssoRefreshPromise = null;
      }
    })();
  }
  return ssoRefreshPromise;
};

const clearLocalAppSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  clearAllMailPasswords();
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const mailPassword = getMailPassword();
    const requestUrl = config.url || '';

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (mailPassword && isLiveMailEndpoint(requestUrl) && !hasGroupSendPayload(config.data)) {
      config.headers['x-mail-password'] = mailPassword;
    } else {
      delete config.headers['x-mail-password'];
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const code = error.response?.data?.code;
    const isMailboxAuthError = code === 'MAILBOX_AUTH_FAILED';
    const needsMailboxLogin =
      code === 'MAILBOX_CREDENTIALS_REQUIRED' ||
      (error.response?.status === 400 &&
        String(error.response?.data?.message || '').toLowerCase().includes('mailbox'));

    // Missing mailbox password: keep JWT session — do not force login page.
    if (needsMailboxLogin) {
      return Promise.reject(error);
    }

    // ATOZAS SSO opens the mailbox with the server-held secret. Sending the
    // user to the password login page clears that session and the inbox then
    // shows "Mailbox login required" even though SSO already succeeded.
    let storedUser = {};
    try {
      storedUser = JSON.parse(localStorage.getItem('user') || '{}') || {};
    } catch (_e) {
      storedUser = {};
    }
    const isAtozasUser =
      storedUser.authProvider === 'atozas' || Boolean(storedUser.atozasUserId);
    // SSO mailboxes use a server-held secret — never bounce them to /login on
    // a transient IMAP auth blip (that looked like a random short logout).
    if (isMailboxAuthError && isAtozasUser) {
      return Promise.reject(error);
    }

    // Wrong mailbox password: clear stored password and ask once more.
    if (isMailboxAuthError) {
      clearAllMailPasswords();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isMailboxAuthError) {
      // During SSO handoff a stale JWT must not force /login — the finish page
      // is about to mint a new session from the fresh ATOZAS cookie.
      if (isAtozasHandoffPath()) {
        clearLocalAppSession();
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      // JWT expiry is NOT an explicit logout. If the ATOZAS SSO cookie is still
      // valid, silently mint a new app token and retry once. Do not set
      // atozas_logged_out here — that flag is reserved for user logout and was
      // blocking homepage / product-card relaunch until cookies were cleared.
      if (originalRequest && !originalRequest._atozasRetry && !wasExplicitAtozasLogout()) {
        originalRequest._atozasRetry = true;
        const refreshed = await refreshSessionFromAtozasSso();
        if (refreshed) {
          const nextToken = localStorage.getItem('token');
          originalRequest.headers = originalRequest.headers || {};
          if (nextToken) {
            originalRequest.headers.Authorization = `Bearer ${nextToken}`;
          }
          return axiosInstance(originalRequest);
        }
      }

      clearLocalAppSession();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
